<template>
    <v-dialog
        v-model="$props.params.restoreDialog"
        :persistent="showProgressBar"
        max-width="600px"
        transition="dialog-bottom-transition"
        @keydown.esc="!showProgressBar ? closeRestoreDialog() : ''"
        @click:outside="!showProgressBar ? closeRestoreDialog() : ''"
    >
        <v-card>
            <v-form @submit.prevent="processRestore()">

                <v-toolbar
                    :class="dialogHeadingClasses()"
                    color="primary"
                    dark
                >
                    <v-icon class="mr-3" v-text="icons.mdiRestore"></v-icon>
                    {{ $gettext('Restore') }}
                    <v-spacer></v-spacer>
                    <v-icon v-show="!showProgressBar" class="mr-2" v-text="icons.mdiClose"
                            @click.native.stop="closeRestoreDialog()"></v-icon>
                    <!--                    <v-icon @click.native.stop="processRestore()" v-text="icons.mdiContentSave" v-show="!showProgressBar"></v-icon>-->
                </v-toolbar>

                <v-divider></v-divider>

                <v-card-text class="mt-4">
                    <v-container>

                        <v-row v-if="!isProcessingRestore">
                            <v-col cols="12">
                                <p v-if="!restoreChannel" class="body-1">
                                    {{ $gettext('Please pick a channel to restore') }}
                                </p>

                                <v-radio-group
                                    v-model="restoreChannel"
                                    :error-messages="restoreChannelErrors"
                                    :placeholder="$gettext('Please choose a restore channel')"
                                    row
                                    @blur="$v.restoreChannel.$touch()"
                                    @change="resetRestoreConfirmation()"
                                    @input="$v.restoreChannel.$touch()"
                                >
                                    <v-radio
                                        v-for="channel in restoreChannelOptions"
                                        :key="channel.key"
                                        :label="channel.text"
                                        :value="channel.key"
                                    ></v-radio>
                                </v-radio-group>
                                <!--<v-select :items="restoreChannelOptions" v-model="restoreChannel" :item-text="'text'" :item-value="'key'" outlined label="Please choose a restore channel"></v-select>-->

                            </v-col>
                        </v-row>

                        <v-row v-if="restoreChannel === 'disk'">
                            <v-col>
                                <!--<p class="body-1">
                                    Please choose your backup file
                                </p>-->

                                <v-file-input
                                    v-model="restoreFile"
                                    :error-messages="restoreFileErrors"
                                    :label="$gettext('Please choose your backup file')"
                                    accept=".zip"
                                    multiple
                                    outlined
                                    prepend-icon="mdi-zip-box"
                                    show-size
                                    @blur="$v.restoreFile.$touch()"
                                    @input="$v.restoreFile.$touch()"
                                ></v-file-input>
                            </v-col>
                        </v-row>

                        <v-row v-else-if="showProgressBar && restoreChannel === 'backup'">
                            <v-col>
                                <progress-bar :text="restoreProgressBarMessage"></progress-bar>
                            </v-col>
                        </v-row>

                        <v-row v-else-if="!showProgressBar && restoreChannel === 'backup'">
                            <v-col>

                                <v-autocomplete
                                    v-model="chosenRestorePoint"
                                    :error-messages="restoreChosenRestorePointErrors"
                                    :items="restorePoints"
                                    label="Please pick a restore point"
                                    no-data-text="Sorry, we don't seem to have any restore points here."
                                    outlined
                                    @blur="$v.chosenRestorePoint.$touch()"
                                    @change="resetRestoreConfirmation()"
                                    @input="$v.chosenRestorePoint.$touch()"
                                >
                                </v-autocomplete>

                                <v-checkbox
                                    v-if="chosenRestorePoint"
                                    v-model="restoreConfirmation"
                                    :error-messages="restoreConfirmationErrors"
                                    :false-value="false"
                                    :true-value="true"
                                    @blur="$v.restoreConfirmation.$touch()"
                                    @input="$v.restoreConfirmation.$touch()"
                                >
                                    <template v-slot:label>
                                        <span>Restore to <span class="font-weight-bold">{{
                                                chosenRestorePoint.replace('.zip', '')
                                            }}</span></span>
                                    </template>
                                </v-checkbox>

                            </v-col>
                        </v-row>
                    </v-container>
                </v-card-text>
                <v-divider></v-divider>

                <v-card-actions v-show="(restoreChannel === 'backup' && !showProgressBar) || (restoreChannel === 'disk')"
                                color="secondary">
                    <v-spacer></v-spacer>
                    <v-btn color="primary" v-text="$gettext('Close')" @click.native.stop="closeRestoreDialog()"></v-btn>
                    <v-btn color="primary" type="submit" v-text="$gettext('Restore')"></v-btn>
                </v-card-actions>
            </v-form>

        </v-card>
        <snackbar ref="snackbar"></snackbar>

    </v-dialog>
</template>
<style type="text/scss">
.add-rule-radio-margin .v-input--selection-controls {
    margin-top: 0;
}

</style>

<script>

import Vue from 'vue'
import {find, findIndex, size} from 'lodash'

import Vuelidate from 'vuelidate'
import {required, minLength, maxLength, between, sameAs, requiredIf} from 'vuelidate/lib/validators'

import MixinDialog from '@/mixins/MixinDialog'
import MixinIcon from '@/mixins/MixinIcon'
import Snackbar from '@/components/Snackbar'
import MixinStringManipulation from '@/mixins/MixinStringManipulation'
import MixinConfigRules from '@/mixins/database/MixinConfigRules'
import MixinLodashPadStart from '@/mixins/lodash/MixinLodashPadStart'
import {mapState, mapWritableState} from 'pinia'
import {useConnectionsStore} from '@/stores/ConnectionsStore'
import {useProgressBarsStore} from '@/stores/ProgressBarStore'
import MixinRestore from '@/mixins/MixinRestore'
import {useRulesStore} from '@/stores/RulesStore'
import {useGroupsStore} from '@/stores/GroupsStore'
import {useCategoriesStore} from '@/stores/CategoriesStore'
import MixinConfig from '@/mixins/database/MixinConfig'
import MixinGenericComponent from '@/mixins/generic/MixinGenericComponent'
import ProgressBar from '@/components/ProgressBar'
import MixinSwalConfirmation from '@/mixins/swal/MixinSwalConfirmation'

Vue.use(Vuelidate)

export default {
    components: {Snackbar, ProgressBar},
    mixins: [MixinConfig, MixinDialog, MixinIcon, MixinStringManipulation, MixinConfigRules, MixinLodashPadStart, MixinRestore, MixinGenericComponent, MixinSwalConfirmation],
    props: {
        params: {
            required: true,
            type: Object | Array,
        },
    },
    data() {
        return {
            restoreProgressBarMessage: this.$gettext('Loading your backups, just a moment...'),
            showProgressBar: false,
            restorePoints: [],
            chosenRestorePoint: '',
            restoreConfirmation: false,
            restoreChannel: '',
            restoreChannelOptions: [
                {'key': 'disk', 'text': this.$gettext('Restore from a file')},
                {'key': 'backup', text: this.$gettext('Restore from a backup')}
            ],
            restoreFile: null,
            isProcessingRestore: false,
        }
    },

    watch: {

        'restoreChannel': function (val) {

            let self = this

            if (val === 'backup' && !self.showProgressBar && !self.restorePoints.length) {
                this.loadRestorePointsForBackup()
            }

        },

    },

    methods: {

        loadRestorePointsForBackup() {

            let self = this

            self.showProgressBar = true

            self.restoreLoadRestorePoints().then((response) => {

                if (response.status) {

                    let rp = response.data.restorePoints
                    let restorePoints

                    if (rp && rp.length > 0) {
                        restorePoints = typeof rp === 'string' ? JSON.parse(rp) : (typeof rp === 'object' ? rp : [])
                    }

                    restorePoints = restorePoints.filter((theRestorePoint) => {
                        return !(theRestorePoint.includes('placeholder.txt'))
                    })

                    self.restorePoints = restorePoints

                }

            }).catch((error) => {
                // TODO: Handle this
                console.log(error)
            }).finally(() => {
                self.showProgressBar = false
            })

        },

        resetRestoreConfirmation() {
            this.restoreConfirmation = false
        },

        async processRestore() {

            let self = this

            self.$v.$touch()

            if (!self.$v.$invalid) {

                self.showProgressBar = true
                self.restoreProgressBarMessage = self.$gettext('Restoring...')
                self.isProcessingRestore = true

                let appComponent = self.genericComponentGetComponent('App')
                const alertErrorOccurred = () => appComponent.$refs.snackbar.showSnackbar(self.$gettext('Sorry! An error occurred and restore was not successful.'), 'error')

                try {

                    let response = await self.restoreProcessRestore()

                    if (response?.status) {

                        if (response.data?.hasOwnProperty('restoreDetails') && response.data.restoreDetails) {

                            response.data.restoreDetails = (typeof response.data.restoreDetails === 'string') ? JSON.parse(response.data.restoreDetails) : response.data.restoreDetails

                            self.configSetVariablesToStores(response.data.restoreDetails, false, false, 'restore')
                            appComponent.$refs.snackbar.showSnackbar(self.$gettext('Configuration restored successfully! Please don\'t forget to save.'))
                            self.resetForm()
                            self.closeRestoreDialog()

                        } else {
                            alertErrorOccurred()
                        }

                    } else {
                        alertErrorOccurred()
                    }

                } catch (error) {
                    // FIXME: REMOVE THIS
                    console.log('error', error)
                    alertErrorOccurred()
                } finally {
                    self.showProgressBar = false
                    self.restoreProgressBarMessage = ''
                    self.isProcessingRestore = false
                    // self.closeRestoreDialog();
                }

            }

        },
        closeRestoreDialog() {
            let self = this
            let appComponent = self.genericComponentGetComponent('App')
            appComponent.closeDialog('restoreDialog')
        },
        resetForm() {
            let self = this
            self.$v.$reset()
            self.chosenRestorePoint = ''
        }
    },
    computed: {

        ...mapState(useConnectionsStore, {
            currentConnection: 'currentConnection',
        }),

        ...mapWritableState(useProgressBarsStore, {
            progressBars: 'progressBars',
        }),
        ...mapWritableState(useRulesStore, {
            rules: 'rules',
        }),
        ...mapWritableState(useGroupsStore, {
            groups: 'groups',
        }),

        ...mapWritableState(useCategoriesStore, {
            categories: 'categories',
            whitelist: 'whitelist',
            blacklist: 'blacklist'
        }),

        restoreChosenRestorePointErrors() {
            let self = this
            let errors = []
            if (!self.$v.chosenRestorePoint.$dirty) return errors
            !self.$v.chosenRestorePoint.required && errors.push(this.$gettext('Please choose a restore point'))
            return errors
        },

        restoreConfirmationErrors() {
            let self = this
            let errors = []
            if (!self.$v.restoreConfirmation.$dirty) return errors
            !self.$v.restoreConfirmation.sameAs && errors.push(this.$gettext('Please confirm.'))
            return errors
        },

        restoreChannelErrors() {
            let self = this
            let errors = []
            if (!self.$v.restoreChannel.$dirty) return errors
            !self.$v.restoreChannel.required && errors.push(this.$gettext('Please choose a channel for restore.'))
            return errors
        },

        restoreFileErrors() {
            let self = this
            let errors = []
            if (!self.$v.restoreFile.$dirty) return errors
            !self.$v.restoreFile.required && errors.push(this.$gettext('Please choose a file.'))
            return errors
        },

    },
    validations: {
        restoreChannel: {
            required,
        },
        chosenRestorePoint: {
            required: requiredIf((vm) => {
                return vm?.restoreChannel === 'backup'
            }),
        },
        restoreConfirmation: {
            sameAs: sameAs((vm) => vm?.restoreChannel === 'backup')
        },
        restoreFile: {
            required: requiredIf((vm) => {
                return vm?.restoreChannel === 'disk'
            }),
        },
    },
}
</script>
